/**
 * ForgotPassword — F10 T10.11.
 *
 * Strona „Zapomnialem hasla". Public scope (`app.<base>`) i tenant scope
 * (`<slug>.app.<base>`) — host rozstrzyga `useHostScope()`. W tenant scope
 * dorzucamy `tenantSlug` z hosta (filter forgot-password do konkretnego tenanta).
 * W public scope wysylamy bez slug-a (backend rozsyla mail per tenant gdzie
 * konto istnieje).
 *
 * Anti-enumeration: zawsze pokazujemy ten sam ekran sukcesu „Wyslalismy link",
 * niezaleznie czy email istnieje. Widac to po response 200 z backendu.
 *
 * Errors:
 *   - 429 RATE_LIMITED → toast „Zbyt wiele prób, spróbuj za godzinę".
 *   - inne (5xx) → backend i tak zwraca 200 anti-enumeration; tu tylko jak
 *     network failure → toast „Cos poszlo nie tak".
 */

import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { useToast } from '@/contexts/ToastContext';
import { useHostScope } from '@/hooks/useHostScope';
import { api } from '@/lib/api';

type State = { kind: 'idle' } | { kind: 'submitting' } | { kind: 'sent'; email: string };

function extractStatus(err: unknown): number | null {
  return (err as { response?: { status?: number } }).response?.status ?? null;
}

function getTenantSlugFromHost(): string | null {
  if (typeof window === 'undefined') return null;
  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  if (parts.length >= 3 && parts[1] === 'app') return parts[0] ?? null;
  return null;
}

export function ForgotPassword() {
  const { t } = useTranslation();
  const toast = useToast();
  const scope = useHostScope();
  const [email, setEmail] = useState('');
  const [state, setState] = useState<State>({ kind: 'idle' });

  const submitting = state.kind === 'submitting';

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setState({ kind: 'submitting' });

    try {
      const body: { email: string; tenantSlug?: string } = { email };
      if (scope === 'tenant') {
        const slug = getTenantSlugFromHost();
        if (slug) body.tenantSlug = slug;
      }
      await api.post('/auth/forgot-password', body);
      setState({ kind: 'sent', email });
    } catch (err) {
      const status = extractStatus(err);
      if (status === 429) {
        toast.show({
          variant: 'warning',
          title: t('auth.forgot.errors.rateLimitedTitle'),
          description: t('auth.forgot.errors.rateLimitedDescription'),
        });
      } else {
        toast.show({
          variant: 'error',
          title: t('auth.forgot.errors.genericTitle'),
          description: t('auth.forgot.errors.genericDescription'),
        });
      }
      setState({ kind: 'idle' });
    }
  };

  if (state.kind === 'sent') {
    return (
      <main className="flex min-h-screen items-center justify-center bg-bg-secondary p-4">
        <div
          className="w-full max-w-md rounded-2xl border border-border-secondary bg-bg-elevated p-8 shadow-xl"
          data-testid="forgot-password-sent"
        >
          <div className="mb-6 text-center">
            <h1 className="text-2xl font-bold text-text-primary">{t('auth.forgot.sentTitle')}</h1>
            <p className="mt-3 text-sm text-text-tertiary">
              {t('auth.forgot.sentDescription', { email: state.email })}
            </p>
          </div>
          <div className="mt-8">
            <Link to="/login">
              <Button variant="secondary" className="w-full">
                {t('auth.forgot.backToLogin')}
              </Button>
            </Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-bg-secondary p-4">
      <div className="w-full max-w-md rounded-2xl border border-border-secondary bg-bg-elevated p-8 shadow-xl">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold text-text-primary">{t('auth.forgot.title')}</h1>
          <p className="mt-2 text-sm text-text-tertiary">{t('auth.forgot.subtitle')}</p>
        </div>

        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <Input
            label={t('auth.forgot.emailLabel')}
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('auth.forgot.emailPlaceholder')}
            disabled={submitting}
            data-testid="forgot-password-email"
          />

          <Button
            type="submit"
            className="w-full"
            loading={submitting}
            data-testid="forgot-password-submit"
          >
            {submitting ? t('auth.forgot.submitting') : t('auth.forgot.submit')}
          </Button>

          <div className="flex items-center justify-center pt-2 text-xs">
            <Link
              to="/login"
              className="text-text-tertiary underline underline-offset-2 hover:text-text-primary"
            >
              {t('auth.forgot.backToLogin')}
            </Link>
          </div>
        </form>
      </div>
    </main>
  );
}
